import React, { useState, useEffect, useCallback } from 'react';
import { Wrench, Shield, Clock, CheckCircle, AlertCircle, MessageSquare, Search, Filter, ChevronDown, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'yellow', icon: Clock },
  in_progress: { label: 'En cours', color: 'blue', icon: AlertCircle },
  resolved: { label: 'Résolu', color: 'green', icon: CheckCircle },
  closed: { label: 'Fermé', color: 'slate', icon: CheckCircle },
};

export default function SAVPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', type: '', search: '' });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.type) params.append('type', filter.type);

      const response = await fetch(`${API_URL}/api/sav-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setRequests(data);
    } catch (error) {
      console.error('Failed to fetch:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filter.status, filter.type]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateStatus = async (requestId, newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/sav-requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      const updated = await response.json();
      setRequests(prev => prev.map(r => r.id === requestId ? updated : r));
      if (selectedRequest?.id === requestId) setSelectedRequest(updated);
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedRequest) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/sav-requests/${selectedRequest.id}/note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: newNote })
      });

      if (!response.ok) throw new Error('Failed to add note');

      const updated = await response.json();
      setSelectedRequest(updated);
      setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
      setNewNote('');
      toast.success('Note ajoutée');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setUpdating(false);
    }
  };

  const filteredRequests = requests.filter(r => {
    if (filter.search) {
      const search = filter.search.toLowerCase();
      return r.reference?.toLowerCase().includes(search) ||
             r.client_name?.toLowerCase().includes(search) ||
             r.subject?.toLowerCase().includes(search);
    }
    return true;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    resolved: requests.filter(r => r.status === 'resolved').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">SAV & Garantie</h1>
        <p className="text-slate-500 text-sm">Gestion des demandes clients</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-slate-500 text-sm">Total</p>
          <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 shadow-sm border border-yellow-100">
          <p className="text-yellow-600 text-sm">En attente</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 shadow-sm border border-blue-100">
          <p className="text-blue-600 text-sm">En cours</p>
          <p className="text-2xl font-bold text-blue-700">{stats.in_progress}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-100">
          <p className="text-green-600 text-sm">Résolus</p>
          <p className="text-2xl font-bold text-green-700">{stats.resolved}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>

          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_CONFIG).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>

          <select
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Tous les types</option>
            <option value="sav">SAV</option>
            <option value="garantie">Garantie</option>
          </select>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-2 space-y-3">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl">
              <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucune demande trouvée</p>
            </div>
          ) : (
            filteredRequests.map(request => {
              const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              const isSelected = selectedRequest?.id === request.id;
              
              return (
                <div
                  key={request.id}
                  onClick={() => setSelectedRequest(request)}
                  className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition ${
                    isSelected ? 'ring-2 ring-yellow-500' : 'hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        request.type === 'garantie' ? 'bg-purple-100' : 'bg-orange-100'
                      }`}>
                        {request.type === 'garantie' ? (
                          <Shield className={`w-5 h-5 ${request.type === 'garantie' ? 'text-purple-600' : 'text-orange-600'}`} />
                        ) : (
                          <Wrench className="w-5 h-5 text-orange-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-slate-500">{request.reference}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <h3 className="font-medium text-slate-800">{request.subject}</h3>
                        <p className="text-sm text-slate-500">{request.client_name}</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(request.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-1">
          {selectedRequest ? (
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-slate-500">{selectedRequest.reference}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  selectedRequest.type === 'garantie' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                }`}>
                  {selectedRequest.type === 'garantie' ? 'Garantie' : 'SAV'}
                </span>
              </div>

              <h2 className="font-bold text-slate-800">{selectedRequest.subject}</h2>
              
              <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                {selectedRequest.description}
              </div>

              <div className="border-t pt-4 space-y-2 text-sm">
                <p><span className="text-slate-500">Client:</span> {selectedRequest.client_name}</p>
                <p><span className="text-slate-500">Email:</span> {selectedRequest.client_email}</p>
                {selectedRequest.machine_name && (
                  <p><span className="text-slate-500">Machine:</span> {selectedRequest.machine_name}</p>
                )}
                {selectedRequest.serial_number && (
                  <p><span className="text-slate-500">N° série:</span> {selectedRequest.serial_number}</p>
                )}
              </div>

              {/* Status change */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Changer le statut</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <button
                      key={key}
                      onClick={() => updateStatus(selectedRequest.id, key)}
                      disabled={updating || selectedRequest.status === key}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        selectedRequest.status === key
                          ? `bg-${val.color}-500 text-white`
                          : `bg-${val.color}-50 text-${val.color}-700 hover:bg-${val.color}-100`
                      } disabled:opacity-50`}
                    >
                      {val.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Notes internes ({selectedRequest.admin_notes?.length || 0})
                </h3>
                
                <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                  {selectedRequest.admin_notes?.map(note => (
                    <div key={note.id} className="bg-slate-50 rounded-lg p-2 text-sm">
                      <p className="text-slate-700">{note.content}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {note.created_by_name} - {new Date(note.created_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Ajouter une note..."
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addNote()}
                  />
                  <button
                    onClick={addNote}
                    disabled={!newNote.trim() || updating}
                    className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Sélectionnez une demande</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
